import { EventConfirmer } from '~/core/events/EventConfirmer';
import { EventQueue } from '~/core/events/EventQueue';
import { isCloseEvent } from '~/core/events/types/eventTypes/CloseEvent';
import { GoToResultEvent, isGoToResultEvent } from '~/core/events/types/eventTypes/GoToResultEvent';
import { isResetQueryEvent } from '~/core/events/types/eventTypes/ResetQueryEvent';
import { isSearchEvent, SearchEvent } from '~/core/events/types/eventTypes/SearchEvent';
import { AvailableConfirmationEvents } from '~/core/events/types/helperTypes/availableConfirmationEvents';
import {
    isConfirmableEvent,
    isSERPEvent,
    SERPConfirmableEvent,
    SERPConfirmationEventType,
    SERPEvent,
    SERPEventType,
} from '~/core/events/types/SERPEvents';

declare global {
    interface Window {
        dataLayer: any[];
        gtag: (...args: any[]) => void;
    }
}

export class GoogleAnalytics {
    private readonly eventConfirmer: EventConfirmer;
    private readonly availableConfirmations = {
        GO_TO_RESULT: SERPConfirmationEventType.GO_TO_RESULT_GA_CONFIRM,
    } as const satisfies AvailableConfirmationEvents;
    private readonly queue: EventQueue;

    constructor() {
        this.eventConfirmer = new EventConfirmer();
        this.processEvent = this.processEvent.bind(this);
        this.queue = new EventQueue(this.processEvent);
        this.registerEventListeners();
    }

    private registerEventListeners = (): void => {
        window.addEventListener(SERPEventType.SEARCH, (event: Event) => {
            if (!isSERPEvent(event) || !isSearchEvent(event)) {
                throw new Error('Search event does not have correct format');
            }

            this.queue.addEvent(event);
        });
        window.addEventListener(SERPEventType.GO_TO_RESULT, (event: Event) => {
            if (!isSERPEvent(event) || !isGoToResultEvent(event)) {
                throw new Error('Go-to-result event does not have correct format');
            }
            (async () => {
                this.queue.addEvent(event);
                await this.queue.processQueue();
                if (!isConfirmableEvent(event)) {
                    return;
                }

                this.handleEventConfirmation(event);
            })();
        });

        window.addEventListener(SERPEventType.RESET_QUERY, (event: Event): void => {
            if (!isSERPEvent(event) || !isResetQueryEvent(event)) {
                throw new Error('Reset query event does not have correct format');
            }
            this.queue.clearQueue();
        });

        window.addEventListener(SERPEventType.CLOSE, (event: Event): void => {
            if (!isSERPEvent(event) || !isCloseEvent(event)) {
                throw new Error('Close event does not have correct format');
            }

            this.handleCloseEvent(event);
        });
    };

    /**
     * callback for queue
     * called when queue is processed
     */
    private processEvent = async (event: SERPEvent): Promise<void> => {
        if (isSearchEvent(event)) {
            await this.handleSearchEvent(event);
            return;
        }
        if (isGoToResultEvent(event)) {
            await this.handleGoToResultEvent(event);
            return;
        }
        if (isCloseEvent(event)) {
            await this.handleCloseEvent(event);
            return;
        }
        throw new Error('Google Analytics does not support this event type');
    };

    private handleSearchEvent = (event: SearchEvent): Promise<boolean> => {
        return new Promise((resolve) => {
            const eventData = {
                sr_search_term: event.detail.data.searchQuery,
                sr_results_count: event.detail.data.totalResultsCount,
            };

            window.dataLayer.push({
                event: 'search_results',
                gtm: { ...eventData },
            });

            if (window.gtag && typeof window.gtag === 'function') {
                window.gtag('event', 'Search Results', eventData);
            }

            resolve(true);
        });
    };

    private handleGoToResultEvent = (event: GoToResultEvent): Promise<boolean> => {
        return new Promise((resolve) => {
            const eventData = {
                sr_search_term: event.detail.data.searchQuery,
                sr_url: event.detail.data.url,
                sr_order: event.detail.data.order,
                sr_result_type: event.detail.data.resultType,
                sr_results_count: event.detail.data.resultsCount,
            };

            window.dataLayer.push({
                event: 'search_result_click',
                gtm: { ...eventData },
            });

            if (window.gtag && typeof window.gtag === 'function') {
                window.gtag('event', 'Search Result Click', eventData);
            }

            resolve(true);
        });
    };

    // Leaving the event handler in the code deliberately for potential re-implementation in the future
    private handleCloseEvent = (event: Event): Promise<boolean> => {
        return new Promise((resolve) => {
            if (!isSERPEvent(event) || !isCloseEvent(event)) {
                throw new Error('Close event does not have correct format');
            }

            resolve(true);
        });
    };

    private handleEventConfirmation = (event: SERPConfirmableEvent): void => {
        this.eventConfirmer.confirmEventDelivery(event.detail.deliveryConfirmations, this.availableConfirmations);
    };
}
